// src/services/errorRecoveryService.ts
import { cookieService } from "./cookieService";
import { jwtLifecycleService } from "./jwtLifecycleService";
import { reportError, reportMessage } from "@/utils/sentry";
import { notificationService } from "@/services/notificationService";

interface RecoveryOptions {
  reason?: string;
  userInitiated?: boolean;
  redirectPath?: string;
  showMessage?: boolean;
}

interface TrappedUserSignals {
  hasInvalidTokens: boolean;
  hasAuthErrors: boolean;
  noUserInterface: boolean;
  repeatedFailures: boolean;
}

/**
 * 错误恢复服务
 *
 * 核心功能：
 * 1. 检测并解决用户被困在已登录页面的情况
 * 2. 强制恢复机制：清除所有状态并确保跳转成功
 * 3. 多重跳转保障，处理各种边界情况
 * 4. 跨标签页状态同步
 * 5. 即将过期警告和处理
 */
class ErrorRecoveryService {
  private readonly STORAGE_PREFIX = "recovery_";
  private readonly CROSS_TAB_KEY = "auth_state_sync";
  private readonly MAX_RETRY_ATTEMPTS = 3;
  private readonly RECOVERY_TIMEOUT_MS = 5000;

  private recoveryInProgress = false;
  private retryCount = 0;
  private lastRecoveryTime = 0;

  constructor() {
    this.initializeCrossTabSync();
  }

  /**
   * 检测用户是否被困在系统中
   */
  public detectTrappedUser(): boolean {
    const signals = this.gatherTrappedUserSignals();

    const isTrapped =
      (signals.hasInvalidTokens && signals.noUserInterface) ||
      (signals.hasAuthErrors && signals.repeatedFailures);

    if (isTrapped) {
      console.warn("Trapped user detected:", signals);
      reportMessage("检测到用户被困情况", "warning", {
        signals: JSON.stringify(signals),
        currentPath: window.location.pathname,
        timestamp: new Date().toISOString(),
      });
    }

    return isTrapped;
  }

  /**
   * 收集用户被困的信号
   */
  private gatherTrappedUserSignals(): TrappedUserSignals {
    // 检查token状态
    const accessToken = cookieService.get("jwt_token");
    const refreshToken = cookieService.get("refresh_token");
    const hasInvalidTokens = !accessToken && !refreshToken;

    // 检查是否有认证错误记录
    const authErrors = this.getStorageValue("auth_errors", 0);
    const hasAuthErrors = authErrors > 2;

    // 检查用户界面是否缺失（例如：没有登出按钮）
    const noUserInterface = this.checkMissingUserInterface();

    // 检查是否有重复失败
    const failureCount = this.getStorageValue("failure_count", 0);
    const repeatedFailures = failureCount > 3;

    return {
      hasInvalidTokens,
      hasAuthErrors,
      noUserInterface,
      repeatedFailures,
    };
  }

  /**
   * 检查是否缺少用户界面元素
   */
  private checkMissingUserInterface(): boolean {
    // 检查是否在需要认证的页面但缺少用户操作界面
    const authRequiredPaths = ["/authorize", "/email", "/phone", "/security"];
    const currentPath = window.location.pathname;

    if (authRequiredPaths.some((path) => currentPath.includes(path))) {
      // 在认证页面但找不到用户信息或登出按钮
      const hasUserInfo = !!document.querySelector(
        '[data-user-info], .user-info, [class*="user"]',
      );
      const hasLogoutButton = !!document.querySelector(
        '[data-logout], [class*="logout"], [onclick*="logout"]',
      );

      return !hasUserInfo && !hasLogoutButton;
    }

    return false;
  }

  /**
   * 强制恢复机制（核心方法）
   */
  public async forceRecovery(options: RecoveryOptions = {}): Promise<void> {
    // 防止重复恢复
    if (this.recoveryInProgress) {
      console.debug("Recovery already in progress");
      return;
    }

    // 防止频繁恢复（10秒内最多一次）
    const now = Date.now();
    if (now - this.lastRecoveryTime < 10000) {
      console.debug("Recovery throttled");
      return;
    }

    this.recoveryInProgress = true;
    this.lastRecoveryTime = now;
    this.retryCount = 0;

    try {
      console.log("Starting force recovery", options);

      if (options.showMessage !== false) {
        notificationService.info("error.recovery.systemRecovering");
      }

      // 步骤1: 清除所有认证状态
      await this.clearAllAuthState();

      // 步骤2: 重置错误计数
      this.resetErrorCounters();

      // 步骤3: 通知其他标签页
      this.broadcastStateChange("force_recovery");

      // 步骤4: 强制跳转到登录页
      const targetPath = options.redirectPath || this.getLoginPath();
      await this.ensureRedirect(targetPath);

      // 步骤5: 显示成功消息
      if (options.showMessage !== false) {
        notificationService.dismissAll();
        notificationService.success("error.recovery.systemRecovered");
      }

      reportMessage("强制恢复完成", "info", {
        reason: options.reason || "user_trapped",
        userInitiated: options.userInitiated || false,
        targetPath,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.error("Force recovery failed:", error);
      reportError(error as Error, {
        type: "recovery_error",
        action: "force_recovery",
        reason: options.reason,
      });

      // 最后的兜底：页面刷新
      await this.lastResortRecovery();
    } finally {
      this.recoveryInProgress = false;
      if (options.showMessage !== false) {
        notificationService.dismissAll();
      }
    }
  }

  /**
   * 清除所有认证状态
   */
  private async clearAllAuthState(): Promise<void> {
    try {
      // 1. 停止JWT生命周期管理
      jwtLifecycleService.stop();

      // 2. 清除所有Cookie
      cookieService.clearAll();

      // 3. 清除localStorage
      this.clearStorageByPrefix("auth_");
      this.clearStorageByPrefix("jwt_");
      this.clearStorageByPrefix("user_");
      this.clearStorageByPrefix("token_");

      // 4. 清除sessionStorage
      this.clearSessionStorageByPrefix("auth_");
      this.clearSessionStorageByPrefix("jwt_");
      this.clearSessionStorageByPrefix("user_");
      this.clearSessionStorageByPrefix("token_");

      // 5. 清除Pinia store（如果可访问）
      if (window.pinia) {
        try {
          // 重置所有store
          Object.keys(window.pinia.state.value).forEach((storeId) => {
            if (storeId.includes("auth") || storeId.includes("user")) {
              const store = window.pinia._s.get(storeId);
              if (store && typeof store.$reset === "function") {
                store.$reset();
              }
            }
          });
        } catch (e) {
          console.debug("Failed to reset Pinia stores:", e);
        }
      }

      console.log("All auth state cleared successfully");
    } catch (error) {
      console.error("Failed to clear auth state:", error);
      throw error;
    }
  }

  /**
   * 多重跳转保障
   */
  public async ensureRedirect(targetPath: string): Promise<void> {
    const methods = [
      () => this.redirectWithRouter(targetPath),
      () => this.redirectWithLocation(targetPath),
      () => this.redirectWithReplace(targetPath),
      () => this.redirectWithLink(targetPath),
    ];

    for (
      let i = 0;
      i < methods.length && this.retryCount < this.MAX_RETRY_ATTEMPTS;
      i++
    ) {
      try {
        await this.attemptRedirect(methods[i], targetPath, i + 1);

        // 等待验证跳转是否成功
        await this.waitForRedirection(targetPath);

        if (this.isRedirectSuccessful(targetPath)) {
          console.log(`Redirect successful using method ${i + 1}`);
          return;
        }
      } catch (error) {
        console.warn(`Redirect method ${i + 1} failed:`, error);
        this.retryCount++;
      }
    }

    // 所有方法都失败，使用最后的兜底方案
    console.error("All redirect methods failed, using last resort");
    await this.lastResortRecovery();
  }

  /**
   * 尝试跳转方法
   */
  private async attemptRedirect(
    method: () => Promise<void> | void,
    targetPath: string,
    methodNumber: number,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error(`Redirect method ${methodNumber} timeout`));
      }, this.RECOVERY_TIMEOUT_MS);

      try {
        const result = method();
        if (result instanceof Promise) {
          result
            .then(() => {
              clearTimeout(timeout);
              resolve();
            })
            .catch(reject);
        } else {
          clearTimeout(timeout);
          resolve();
        }
      } catch (error) {
        clearTimeout(timeout);
        reject(error);
      }
    });
  }

  /**
   * 使用Router跳转
   */
  private async redirectWithRouter(targetPath: string): Promise<void> {
    if (window.router) {
      await window.router.replace(targetPath);
    } else {
      throw new Error("Router not available");
    }
  }

  /**
   * 使用window.location跳转
   */
  private redirectWithLocation(targetPath: string): void {
    const fullUrl = this.buildFullUrl(targetPath);
    window.location.href = fullUrl;
  }

  /**
   * 使用window.location.replace跳转
   */
  private redirectWithReplace(targetPath: string): void {
    const fullUrl = this.buildFullUrl(targetPath);
    window.location.replace(fullUrl);
  }

  /**
   * 使用临时a标签跳转
   */
  private redirectWithLink(targetPath: string): void {
    const fullUrl = this.buildFullUrl(targetPath);
    const link = document.createElement("a");
    link.href = fullUrl;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * 构建完整URL
   */
  private buildFullUrl(targetPath: string): string {
    const baseUrl = import.meta.env.VITE_BASE_URL || "/";
    const cleanBaseUrl = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
    const cleanTargetPath = targetPath.startsWith("/")
      ? targetPath.slice(1)
      : targetPath;

    return `${window.location.origin}${cleanBaseUrl}/${cleanTargetPath}`;
  }

  /**
   * 等待跳转完成
   */
  private async waitForRedirection(targetPath: string): Promise<void> {
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (this.isRedirectSuccessful(targetPath)) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);

      setTimeout(() => {
        clearInterval(checkInterval);
        resolve();
      }, 2000);
    });
  }

  /**
   * 检查跳转是否成功
   */
  private isRedirectSuccessful(targetPath: string): boolean {
    const currentPath = window.location.pathname;
    return currentPath.includes(targetPath.replace(/^\//, ""));
  }

  /**
   * 最后的兜底方案：页面刷新
   */
  private async lastResortRecovery(): Promise<void> {
    try {
      console.warn("Using last resort recovery: page reload");

      // 设置恢复标记
      sessionStorage.setItem("recovery_attempted", "true");

      // 刷新到登录页
      const loginUrl = this.buildFullUrl(this.getLoginPath());
      window.location.href = loginUrl;

      // 如果上面没有工作，强制刷新当前页面
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error("Last resort recovery failed:", error);
      // 最终的最终方案：显示手动操作提示
      this.showManualRecoveryInstructions();
    }
  }

  /**
   * 显示手动恢复指导
   */
  private showManualRecoveryInstructions(): void {
    const instructions = document.createElement("div");
    instructions.innerHTML = `
      <div style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); z-index: 9999; display: flex; align-items: center; justify-content: center; color: white; font-family: Arial, sans-serif;">
        <div style="background: white; color: black; padding: 20px; border-radius: 8px; max-width: 400px; text-align: center;">
          <h3>系统恢复失败</h3>
          <p>请手动执行以下操作：</p>
          <ol style="text-align: left;">
            <li>清除浏览器缓存和Cookie</li>
            <li>关闭所有标签页</li>
            <li>重新打开浏览器</li>
            <li>访问登录页面</li>
          </ol>
          <button onclick="window.location.reload()" style="margin-top: 10px; padding: 8px 16px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer;">
            刷新页面
          </button>
        </div>
      </div>
    `;
    document.body.appendChild(instructions);
  }

  /**
   * 即将过期警告
   */
  public showExpiryWarning(daysRemaining: number): void {
    notificationService.warning("error.recovery.expiryWarning", {
      params: { days: daysRemaining },
      duration: 10000,
      key: "expiry-warning",
    });

    reportMessage("显示过期警告", "info", {
      daysRemaining,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * 跨标签页状态同步
   */
  private initializeCrossTabSync(): void {
    window.addEventListener("storage", (event) => {
      if (event.key === this.CROSS_TAB_KEY) {
        try {
          const data = JSON.parse(event.newValue || "{}");
          this.handleCrossTabMessage(data);
        } catch (error) {
          console.error("Failed to parse cross-tab message:", error);
        }
      }
    });

    // 监听页面隐藏/显示，同步状态
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) {
        this.syncWithOtherTabs();
      }
    });
  }

  /**
   * 处理跨标签页消息
   */
  private handleCrossTabMessage(data: unknown): void {
    switch (data.type) {
      case "force_recovery":
        if (!this.recoveryInProgress) {
          this.forceRecovery({
            reason: "cross_tab_recovery",
            showMessage: false,
          });
        }
        break;
      case "token_updated":
        // 其他标签页更新了token，重新检查本页面状态
        this.syncWithOtherTabs();
        break;
    }
  }

  /**
   * 广播状态变化
   */
  private broadcastStateChange(type: string, data?: unknown): void {
    try {
      localStorage.setItem(
        this.CROSS_TAB_KEY,
        JSON.stringify({
          type,
          data,
          timestamp: Date.now(),
        }),
      );

      // 立即删除，确保下次可以触发
      setTimeout(() => {
        localStorage.removeItem(this.CROSS_TAB_KEY);
      }, 100);
    } catch (error) {
      console.error("Failed to broadcast state change:", error);
    }
  }

  /**
   * 与其他标签页同步
   */
  private syncWithOtherTabs(): void {
    // 检查当前页面的认证状态，如果发现问题则触发恢复
    const hasValidTokens = !!(
      cookieService.get("jwt_token") || cookieService.get("refresh_token")
    );

    if (!hasValidTokens && this.requiresAuth()) {
      this.forceRecovery({
        reason: "sync_check_failed",
        showMessage: false,
      });
    }
  }

  /**
   * 检查当前页面是否需要认证
   */
  private requiresAuth(): boolean {
    const authRequiredPaths = ["/authorize", "/email", "/phone", "/security"];
    const currentPath = window.location.pathname;
    return authRequiredPaths.some((path) => currentPath.includes(path));
  }

  /**
   * 获取登录页面路径
   */
  private getLoginPath(): string {
    const baseUrl = import.meta.env.VITE_BASE_URL || "/";
    return baseUrl.endsWith("/") ? `${baseUrl}login` : `${baseUrl}/login`;
  }

  /**
   * 重置错误计数器
   */
  private resetErrorCounters(): void {
    this.removeStorageValue("auth_errors");
    this.removeStorageValue("failure_count");
    this.removeStorageValue("retry_count");
  }

  /**
   * 工具方法：存储操作
   */
  private getStorageValue<T>(key: string, defaultValue: T = null as T): T {
    try {
      const value = localStorage.getItem(`${this.STORAGE_PREFIX}${key}`);
      return value ? JSON.parse(value) : defaultValue;
    } catch {
      return defaultValue;
    }
  }

  private setStorageValue(key: string, value: unknown): void {
    try {
      localStorage.setItem(
        `${this.STORAGE_PREFIX}${key}`,
        JSON.stringify(value),
      );
    } catch (error) {
      console.error("Failed to set storage value:", error);
    }
  }

  private removeStorageValue(key: string): void {
    try {
      localStorage.removeItem(`${this.STORAGE_PREFIX}${key}`);
    } catch (error) {
      console.error("Failed to remove storage value:", error);
    }
  }

  private clearStorageByPrefix(prefix: string): void {
    try {
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith(prefix)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error("Failed to clear storage by prefix:", error);
    }
  }

  private clearSessionStorageByPrefix(prefix: string): void {
    try {
      Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith(prefix)) {
          sessionStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error("Failed to clear session storage by prefix:", error);
    }
  }

  /**
   * 获取服务状态（调试用）
   */
  public getStatus(): {
    recoveryInProgress: boolean;
    retryCount: number;
    lastRecoveryTime: number;
    trappedSignals: TrappedUserSignals;
  } {
    return {
      recoveryInProgress: this.recoveryInProgress,
      retryCount: this.retryCount,
      lastRecoveryTime: this.lastRecoveryTime,
      trappedSignals: this.gatherTrappedUserSignals(),
    };
  }
}

// 导出单例实例
export const errorRecoveryService = new ErrorRecoveryService();

// 导出类和接口以便测试
export { ErrorRecoveryService, type RecoveryOptions, type TrappedUserSignals };
